import React, { useCallback, useEffect, useState } from 'react';
import api from '../services/api';
import { Campaign, EmailTemplate, SenderProfile, Contact } from '../types';

// Loading Skeleton
const SkeletonRow: React.FC = () => (
  <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
    <td style={{ padding: '16px 24px' }}><div style={{ height: '16px', background: '#e5e7eb', borderRadius: '4px', width: '60%' }} /></td>
    <td style={{ padding: '16px 24px' }}><div style={{ height: '24px', background: '#e5e7eb', borderRadius: '9999px', width: '80px' }} /></td>
    <td style={{ padding: '16px 24px' }}><div style={{ height: '16px', background: '#e5e7eb', borderRadius: '4px', width: '40%' }} /></td>
    <td style={{ padding: '16px 24px' }}><div style={{ height: '16px', background: '#e5e7eb', borderRadius: '4px', width: '40%' }} /></td>
    <td style={{ padding: '16px 24px' }}><div style={{ height: '16px', background: '#e5e7eb', borderRadius: '4px', width: '40%' }} /></td>
    <td style={{ padding: '16px 24px' }}><div style={{ height: '32px', background: '#e5e7eb', borderRadius: '6px', width: '100px' }} /></td>
  </tr>
);

const Campaigns: React.FC = () => {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [senderProfiles, setSenderProfiles] = useState<SenderProfile[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState<Campaign | null>(null);
  const [search, setSearch] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [newCampaign, setNewCampaign] = useState({
    name: '',
    template_id: '',
    sender_profile_id: '',
    contact_ids: [] as string[],
  });

  const fetchCampaigns = useCallback(async () => {
    try {
      const params: any = {};
      if (search) params.search = search;
      if (selectedStatus) params.status = selectedStatus;
      const response = await api.get('/campaigns', params);
      setCampaigns(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch campaigns', error);
    } finally {
      setLoading(false);
    }
  }, [search, selectedStatus]);

  useEffect(() => {
    fetchCampaigns();
    fetchTemplates();
    fetchSenderProfiles();
    fetchContacts();
  }, [fetchCampaigns]);

  const fetchTemplates = async () => {
    try {
      const response = await api.get('/templates', { active_only: true });
      setTemplates(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch templates', error);
    }
  };

  const fetchSenderProfiles = async () => {
    try {
      const response = await api.get('/sender-profiles');
      setSenderProfiles(response.data || []);
    } catch (error) {
      console.error('Failed to fetch sender profiles', error);
    }
  };

  const fetchContacts = async () => {
    try {
      const response = await api.get('/contacts', { exclude_spam: true, per_page: 1000 });
      setContacts(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch contacts', error);
    }
  };

  const handleSaveCampaign = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editingCampaign) {
        await api.put(`/campaigns/${editingCampaign.id}`, {
          name: newCampaign.name,
          template_id: newCampaign.template_id ? parseInt(newCampaign.template_id) : undefined,
          sender_profile_id: newCampaign.sender_profile_id ? parseInt(newCampaign.sender_profile_id) : undefined,
        });
      } else {
        await api.post('/campaigns', {
          ...newCampaign,
          contact_ids: newCampaign.contact_ids.map(id => parseInt(id)),
        });
      }
      setShowCreateModal(false);
      setEditingCampaign(null);
      setNewCampaign({ name: '', template_id: '', sender_profile_id: '', contact_ids: [] });
      fetchCampaigns();
    } catch (error: any) {
      alert(error.response?.data?.error || (editingCampaign ? 'Failed to update campaign' : 'Failed to create campaign'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditCampaign = (campaign: Campaign) => {
    setEditingCampaign(campaign);
    setNewCampaign({
      name: campaign.name,
      template_id: campaign.template_id ? String(campaign.template_id) : '',
      sender_profile_id: campaign.sender_profile_id ? String(campaign.sender_profile_id) : '',
      contact_ids: [],
    });
    setShowCreateModal(true);
  };

  const handleSendCampaign = async (id: number) => {
    if (!window.confirm('Are you sure you want to send this campaign?')) return;
    try {
      await api.post(`/campaigns/${id}/send`, {});
      alert('Campaign sending started!');
      fetchCampaigns();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to send campaign');
    }
  };

  const handlePauseCampaign = async (id: number) => {
    if (!window.confirm('Are you sure you want to pause this campaign?')) return;
    try {
      await api.post(`/campaigns/${id}/pause`, {});
      alert('Campaign paused');
      fetchCampaigns();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to pause campaign');
    }
  };

  const handleResumeCampaign = async (id: number) => {
    if (!window.confirm('Are you sure you want to resume this campaign?')) return;
    try {
      await api.post(`/campaigns/${id}/resume`, {});
      alert('Campaign resumed');
      fetchCampaigns();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to resume campaign');
    }
  };

  const handleDeleteCampaign = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this campaign?')) return;
    try {
      await api.delete(`/campaigns/${id}`);
      fetchCampaigns();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to delete campaign');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return { bg: '#d1fae5', text: '#065f46' };
      case 'sending': return { bg: '#fef3c7', text: '#92400e' };
      case 'failed': return { bg: '#fee2e2', text: '#991b1b' };
      case 'paused': return { bg: '#e5e7eb', text: '#374151' };
      default: return { bg: '#dbeafe', text: '#1e40af' };
    }
  };

  return (
    <div style={{ padding: '24px', maxWidth: '1400px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h1 style={{ fontSize: '1.875rem', fontWeight: 700, color: '#111827', margin: '0 0 4px 0' }}>Campaigns</h1>
          <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>Create and manage your email campaigns</p>
        </div>
        <button 
          onClick={() => {
            setEditingCampaign(null);
            setNewCampaign({ name: '', template_id: '', sender_profile_id: '', contact_ids: [] });
            setShowCreateModal(true);
          }}
          style={{ 
            padding: '12px 20px', 
            background: '#3b82f6', 
            color: 'white', 
            border: 'none', 
            borderRadius: '8px', 
            cursor: 'pointer',
            fontSize: '0.875rem',
            fontWeight: 500,
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <span>+</span> Create Campaign
        </button>
      </div>

      {/* Filters */}
      <div style={{ 
        display: 'flex', 
        gap: '12px', 
        marginBottom: '24px',
        flexWrap: 'wrap'
      }}>
        <div style={{ position: 'relative', flex: 1, minWidth: '250px' }}>
          <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }}>🔍</span>
          <input
            type="text"
            placeholder="Search campaigns..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ 
              padding: '10px 12px 10px 40px', 
              flex: 1,
              width: '100%',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              fontSize: '0.875rem',
              outline: 'none'
            }}
          />
        </div>
        <select 
          value={selectedStatus} 
          onChange={(e) => setSelectedStatus(e.target.value)}
          style={{ 
            padding: '10px 16px',
            border: '1px solid #d1d5db',
            borderRadius: '8px',
            fontSize: '0.875rem',
            background: 'white',
            minWidth: '150px'
          }}
        >
          <option value="">All Status</option>
          <option value="draft">Draft</option>
          <option value="scheduled">Scheduled</option>
          <option value="sending">Sending</option>
          <option value="completed">Completed</option>
          <option value="paused">Paused</option>
          <option value="failed">Failed</option>
        </select>
      </div>

      {/* Table */}
      <div style={{ 
        background: 'white', 
        borderRadius: '12px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        border: '1px solid #e5e7eb',
        overflow: 'hidden'
      }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                <th style={{ padding: '12px 24px', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Name</th>
                <th style={{ padding: '12px 24px', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Status</th>
                <th style={{ padding: '12px 24px', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Recipients</th>
                <th style={{ padding: '12px 24px', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Sent</th>
                <th style={{ padding: '12px 24px', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Failed</th>
                <th style={{ padding: '12px 24px', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <>
                  <SkeletonRow />
                  <SkeletonRow />
                  <SkeletonRow />
                </>
              ) : campaigns.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ padding: '48px 24px', textAlign: 'center' }}>
                    <div style={{ fontSize: '3rem', marginBottom: '16px' }}>📧</div>
                    <h3 style={{ fontSize: '1rem', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>
                      No campaigns yet
                    </h3>
                    <p style={{ color: '#6b7280', marginBottom: '24px', fontSize: '0.875rem' }}>
                      Create your first email campaign to get started
                    </p>
                    <button 
                      onClick={() => setShowCreateModal(true)}
                      style={{ 
                        padding: '10px 20px', 
                        background: '#3b82f6', 
                        color: 'white', 
                        border: 'none', 
                        borderRadius: '8px',
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        cursor: 'pointer'
                      }}
                    >
                      Create Campaign
                    </button>
                  </td>
                </tr>
              ) : (
                campaigns.map((campaign) => {
                  const statusColors = getStatusColor(campaign.status);
                  return (
                    <tr key={campaign.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <td style={{ padding: '16px 24px', fontWeight: 500, color: '#111827' }}>{campaign.name}</td>
                      <td style={{ padding: '16px 24px' }}>
                        <span style={{ 
                          padding: '6px 12px', 
                          borderRadius: '9999px',
                          background: statusColors.bg,
                          color: statusColors.text,
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          textTransform: 'capitalize'
                        }}>
                          {campaign.status}
                        </span>
                      </td>
                      <td style={{ padding: '16px 24px', color: '#6b7280' }}>{campaign.total_recipients}</td>
                      <td style={{ padding: '16px 24px', color: '#6b7280' }}>{campaign.sent_count}</td>
                      <td style={{ padding: '16px 24px', color: '#6b7280' }}>{campaign.failed_count}</td>
                      <td style={{ padding: '16px 24px' }}>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          {(campaign.status === 'draft' || campaign.status === 'scheduled') && (
                            <button
                              onClick={() => handleEditCampaign(campaign)}
                              style={{
                                padding: '8px 16px',
                                background: '#eff6ff',
                                color: '#2563eb',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '0.75rem',
                                fontWeight: 500
                              }}
                            >
                              Edit
                            </button>
                          )}
                          {campaign.status === 'sending' && (
                            <button
                              onClick={() => handlePauseCampaign(campaign.id)}
                              style={{
                                padding: '8px 16px',
                                background: '#fef3c7',
                                color: '#92400e',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '0.75rem',
                                fontWeight: 500
                              }}
                            >
                              Pause
                            </button>
                          )}
                          {campaign.status === 'paused' && (
                            <button
                              onClick={() => handleResumeCampaign(campaign.id)}
                              style={{
                                padding: '8px 16px',
                                background: '#dbeafe',
                                color: '#1e40af',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '0.75rem',
                                fontWeight: 500
                              }}
                            >
                              Resume
                            </button>
                          )}
                          {campaign.status === 'draft' && (
                            <button 
                              onClick={() => handleSendCampaign(campaign.id)}
                              style={{ 
                                padding: '8px 16px', 
                                background: '#f0fdf4', 
                                color: '#16a34a', 
                                border: 'none', 
                                borderRadius: '6px', 
                                cursor: 'pointer',
                                fontSize: '0.75rem',
                                fontWeight: 500
                              }}
                            >
                              Send
                            </button>
                          )}
                          <button 
                            onClick={() => handleDeleteCampaign(campaign.id)}
                            style={{ 
                              padding: '8px 16px', 
                              background: '#fef2f2', 
                              color: '#dc2626', 
                              border: 'none', 
                              borderRadius: '6px', 
                              cursor: 'pointer',
                              fontSize: '0.75rem',
                              fontWeight: 500
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showCreateModal && (
        <div style={{ 
          position: 'fixed', 
          top: 0, left: 0, right: 0, bottom: 0, 
          background: 'rgba(0,0,0,0.5)', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{ 
            background: 'white', 
            padding: '32px', 
            borderRadius: '16px', 
            width: '100%',
            maxWidth: '600px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: 600, color: '#111827', margin: '0 0 24px 0' }}>
              {editingCampaign ? 'Edit Campaign' : 'Create Campaign'}
            </h2>
            <form onSubmit={handleSaveCampaign}>
              <div style={{ marginBottom: '20px' }}>
                <label htmlFor="campaignName" style={{ display: 'block', marginBottom: '6px', fontSize: '0.875rem', fontWeight: 500, color: '#374151' }}>
                  Campaign Name *
                </label>
                <input
                  id="campaignName"
                  type="text"
                  required
                  value={newCampaign.name}
                  onChange={(e) => setNewCampaign({...newCampaign, name: e.target.value})}
                  style={{ 
                    width: '100%', 
                    padding: '10px 14px', 
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '0.875rem',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                  placeholder="e.g., Q4 Newsletter"
                />
              </div>
              
              <div style={{ marginBottom: '20px' }}>
                <label htmlFor="templateSelect" style={{ display: 'block', marginBottom: '6px', fontSize: '0.875rem', fontWeight: 500, color: '#374151' }}>
                  Email Template *
                </label>
                <select
                  id="templateSelect"
                  required
                  value={newCampaign.template_id}
                  onChange={(e) => setNewCampaign({...newCampaign, template_id: e.target.value})}
                  style={{ 
                    width: '100%', 
                    padding: '10px 14px', 
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '0.875rem',
                    outline: 'none',
                    background: 'white',
                    boxSizing: 'border-box'
                  }}
                >
                  <option value="">Select a template</option>
                  {templates.map(template => (
                    <option key={template.id} value={template.id}>{template.name}</option>
                  ))}
                </select>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label htmlFor="senderSelect" style={{ display: 'block', marginBottom: '6px', fontSize: '0.875rem', fontWeight: 500, color: '#374151' }}>
                  Sender Profile *
                </label>
                <select
                  id="senderSelect"
                  required
                  value={newCampaign.sender_profile_id}
                  onChange={(e) => setNewCampaign({...newCampaign, sender_profile_id: e.target.value})}
                  style={{ 
                    width: '100%', 
                    padding: '10px 14px', 
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '0.875rem',
                    outline: 'none',
                    background: 'white',
                    boxSizing: 'border-box'
                  }}
                >
                  <option value="">Select a sender</option>
                  {senderProfiles.map(profile => (
                    <option key={profile.id} value={profile.id}>{profile.name} ({profile.from_email})</option>
                  ))}
                </select>
              </div>

              {!editingCampaign && (
              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.875rem', fontWeight: 500, color: '#374151' }}>
                  Recipients * ({newCampaign.contact_ids.length} selected)
                </label>
                <div style={{ 
                  maxHeight: '200px', 
                  overflow: 'auto', 
                  border: '1px solid #d1d5db', 
                  borderRadius: '8px',
                  padding: '12px',
                  background: '#f9fafb'
                }}>
                  {contacts.length === 0 ? (
                    <p style={{ color: '#6b7280', fontSize: '0.875rem', margin: 0 }}>No contacts available</p>
                  ) : (
                    contacts.map(contact => (
                      <label key={contact.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={newCampaign.contact_ids.includes(String(contact.id))}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setNewCampaign({
                                ...newCampaign, 
                                contact_ids: [...newCampaign.contact_ids, String(contact.id)]
                              });
                            } else {
                              setNewCampaign({
                                ...newCampaign, 
                                contact_ids: newCampaign.contact_ids.filter(id => id !== String(contact.id))
                              });
                            }
                          }}
                        />
                        <span style={{ fontSize: '0.875rem', color: '#374151' }}>
                          {contact.email} {contact.first_name && `(${contact.first_name} ${contact.last_name})`}
                        </span>
                      </label>
                    ))
                  )}
                </div>
              </div>
              )}

              <div style={{ display: 'flex', gap: '12px' }}>
                <button 
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setEditingCampaign(null);
                    setNewCampaign({ name: '', template_id: '', sender_profile_id: '', contact_ids: [] });
                  }}
                  disabled={submitting}
                  style={{ 
                    flex: 1, 
                    padding: '12px', 
                    background: '#f3f4f6', 
                    color: '#374151',
                    border: 'none', 
                    borderRadius: '8px',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    cursor: submitting ? 'not-allowed' : 'pointer',
                    opacity: submitting ? 0.6 : 1
                  }}
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  disabled={(!editingCampaign && newCampaign.contact_ids.length === 0) || submitting}
                  style={{ 
                    flex: 1, 
                    padding: '12px', 
                    background: (((!editingCampaign && newCampaign.contact_ids.length === 0) || submitting) ? '#9ca3af' : '#3b82f6'), 
                    color: 'white', 
                    border: 'none', 
                    borderRadius: '8px',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    cursor: (((!editingCampaign && newCampaign.contact_ids.length === 0) || submitting) ? 'not-allowed' : 'pointer'),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                >
                  {submitting ? (
                    <>
                      <span style={{ 
                        width: '16px', 
                        height: '16px', 
                        border: '2px solid rgba(255,255,255,0.3)', 
                        borderTopColor: 'white', 
                        borderRadius: '50%', 
                        animation: 'spin 1s linear infinite',
                        display: 'inline-block'
                      }} />
                      Creating...
                    </>
                  ) : (
                    editingCampaign ? 'Update Campaign' : 'Create Campaign'
                  )}
                </button>
              </div>
            </form>

            <style>{`
              @keyframes spin {
                to { transform: rotate(360deg); }
              }
            `}</style>
          </div>
        </div>
      )}
    </div>
  );
};

export default Campaigns;
